import React, { useEffect, useState, useRef } from 'react';
import { useMemo } from 'react';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Skeleton from '@material-ui/lab/Skeleton';
import ExerciseSearchbar from './searchExercise';
import { useSelector, useDispatch } from "react-redux"
import ExerciseEditDialog from './ExerciseEditDialog';



const useStyles = makeStyles((theme) => ({
  libraryHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 6,
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    paddingLeft: 'clamp(10px, calc(2px + 0.625vw), 16px)',
    paddingRight: 'clamp(10px, calc(2px + 0.625vw), 16px)',
    paddingTop: 'clamp(8px, calc(1px + 0.55vw), 12px)',
    paddingBottom: 'clamp(8px, calc(1px + 0.55vw), 12px)',
    boxSizing: 'border-box',
    borderBottom: '2px solid #FF7F27',
  },
  libraryHeaderTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem) !important',
    position: 'absolute',
    left: 'clamp(10px, calc(2px + 0.625vw), 16px)',
    paddingLeft: 5,
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
  },
  addButton: {
    color: "#FF7F27", 
    backgroundColor: '#F0F8FF',
    borderRadius: 12,
    marginBottom: 10,
    '&:hover': {
      backgroundColor: '#e6eaf0', // light grey that fits the theme
      color: '#FF7F27',
      fontWeight: 'bold',
      maxHeight: 30,
      zIndex: 2
    },
  },
  cardFiltered: {
    height: '97%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardOriginal: {
    height: '97%',
    borderRadius: 18,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0),
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
  },
  cardClickable: {
    cursor: 'pointer',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      boxShadow: '0 0 0 1px #FF7F27, 0 4px 16px rgba(255,127,39,0.18)',
      borderColor: '#FF7F27',
    },
  },
  cardMedia: {
    paddingTop: '56.25%', // 16:9
  },
  exerciseTypographyCardStyling: {
    color: "#002D55",
    textAlign: "center",
    marginBottom: 0,
    fontSize: 'clamp(0.68rem, calc(2px + 0.722vw), 1.0rem) !important',
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  footer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(6),
  },
  orangeSelect: {
    background: '#fff',
    color: '#002D55',
    fontWeight: 500,
    fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
    border: '1px solid #ccc',
    borderRadius: 14,
    padding: 'clamp(2.3px, 0.29vw, 3.5px) clamp(12px, 0.15vw, 18px)',
    paddingLeft: '0px !important', 
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    '& .MuiSelect-select': {
      padding: 'clamp(0px, 0.29vw, 0px) clamp(12px, 0.15vw, 24px) !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      background: 'transparent',
    },
    '&:focus': {
      borderColor: '#FF7F27',
      outline: '#FF7F27 solid 1px',
    },
    '&:hover': {
      borderColor: '#FF7F27',
    },
  },
  largeCheckbox: {
    fontSize: 'clamp(20px, 1.7vw, 32px)',
  },
  menuItem: {
    fontSize: 'clamp(0.69rem, 0.69vw, 0.93rem)',
    color: '#002D55',
  },
  pickerRootCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    margin: '14px 16px 16px',
    paddingBottom: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      margin: '10px 10px 12px',
    },
  },
  pickerMain: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: 14,
  },
}));

const FrequencyType = [
  {
    value: "daily",
    label: "Daily"
  },
  {
    value: "weekly",
    label: "Weekly"
  },
];

const MUSCLEWIKI_RECENT_EXERCISES_KEY = 'vp_muscleWikiRecentExercises';
const MUSCLEWIKI_PAGE_SIZE = 50;

const getRecentMuscleWikiExercises = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(MUSCLEWIKI_RECENT_EXERCISES_KEY) || '[]');
    return Array.isArray(cached) ? cached.filter(exercise => exercise?.sourceExerciseId && exercise?.img) : [];
  } catch (error) {
    return [];
  }
};

export default function ExerciseLibrary({
  onAddExercise,
  exercises: libraryExercises = [],
  phoneUploadContext = null,
  initialFilters = null,
  initialFiltersKey = null,
  onExerciseSelect = null,
  selectionActionLabel = 'Use',
  title = 'Exercise library',
}) {
  const classes = useStyles();
  const token = useSelector(state => state.auth.token);
  const isPickerMode = typeof onExerciseSelect === 'function';
  const [isLoading, setIsLoading] = useState(false);
  const [exerciseSource, setExerciseSource] = useState('internal');
  const [externalExercises, setExternalExercises] = useState([]);
  const [muscleWikiExercises, setMuscleWikiExercises] = useState([]);
  const [ascendExerciseTypes, setAscendExerciseTypes] = useState([]);
  const [ascendEquipments, setAscendEquipments] = useState([]);
  const [ascendRegions, setAscendRegions] = useState([]);
  const [ascendCursor, setAscendCursor] = useState(null);
  const [ascendNextCursor, setAscendNextCursor] = useState(null);
  const [ascendHasMore, setAscendHasMore] = useState(false);
  const [muscleWikiFilters, setMuscleWikiFilters] = useState({});
  const [muscleWikiCategories, setMuscleWikiCategories] = useState([]);
  const [muscleWikiMuscles, setMuscleWikiMuscles] = useState([]);
  const recentMuscleWikiExercisesRef = useRef(getRecentMuscleWikiExercises());
  const [muscleWikiOffset, setMuscleWikiOffset] = useState(0);
  const [muscleWikiHasMore, setMuscleWikiHasMore] = useState(false);
  const [externalSearchError, setExternalSearchError] = useState('');
  const [frequencyType, setFrequencyType] = React.useState();
  const exercises = exerciseSource === 'ascend'
    ? externalExercises
    : exerciseSource === 'musclewiki' ? muscleWikiExercises : libraryExercises;
  const [searchInput, setSearchInput] = useState("");
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterEquipment, setFilterEquipment] = useState('');
  const [filterBodyRegion, setFilterBodyRegion] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalViewOnly, setEditModalViewOnly] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState(new Set());
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [editedParams, setEditedParams] = useState({
    sets: 3,
    repetitions: 10,
    time: 30,
    pause: 15,
    frequencyType: "daily",
    frequency: 1,
    description: "",
    title: "",
    img: "",
    video: "",
    patientInMedia: null,
    doseType: "time",
  });

  useEffect(() => {
    const nextFilters = initialFilters || {};
    setSearchInput(nextFilters.searchInput || '');
    setFilterType(nextFilters.filterType || '');
    setFilterLevel(nextFilters.filterLevel || '');
    setFilterEquipment(nextFilters.filterEquipment || '');
    setFilterBodyRegion(nextFilters.filterBodyRegion || '');
    setSelectedExerciseIds(new Set());
    setAscendCursor(null);
    setAscendNextCursor(null);
    setAscendHasMore(false);
  }, [initialFilters, initialFiltersKey]);

  const handleExerciseSourceChange = (source) => {
    setExerciseSource(source);
    setFilterType('');
    setFilterLevel('');
    setFilterEquipment('');
    setFilterBodyRegion('');
    setSelectedExerciseIds(new Set());
    setMuscleWikiOffset(0);
    setMuscleWikiHasMore(false);
  };

  const rememberMuscleWikiExercise = (exercise) => {
    if (exercise.source !== 'musclewiki' || !exercise.sourceExerciseId || !exercise.img) return;
    const nextRecent = [exercise, ...recentMuscleWikiExercisesRef.current.filter(
      cached => cached.sourceExerciseId !== exercise.sourceExerciseId
    )].slice(0, MUSCLEWIKI_PAGE_SIZE);
    recentMuscleWikiExercisesRef.current = nextRecent;
    try {
      sessionStorage.setItem(MUSCLEWIKI_RECENT_EXERCISES_KEY, JSON.stringify(nextRecent));
    } catch (error) {}
    setMuscleWikiExercises(current => current.map(currentExercise => (
      currentExercise.sourceExerciseId === exercise.sourceExerciseId
        ? { ...currentExercise, img: exercise.img }
        : currentExercise
    )));
  };

  useEffect(() => {
    setMuscleWikiOffset(0);
    setAscendCursor(null);
    setAscendNextCursor(null);
    setAscendHasMore(false);
  }, [searchInput, filterType, filterLevel, filterEquipment, filterBodyRegion]);

  useEffect(() => {
    if (exerciseSource !== 'ascend') {
      return undefined;
    }

    const authToken = sessionStorage.getItem('token') || token;
    if (!authToken) {
      setAscendExerciseTypes([]);
      setAscendEquipments([]);
      setAscendRegions([]);
      return undefined;
    }

    const controller = new AbortController();
    const loadMetadata = async () => {
      try {
        const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
        const requestOptions = {
          headers: { Authorization: `Token ${authToken}` },
          signal: controller.signal,
        };
        const [typesResponse, equipmentsResponse, bodyPartsResponse] = await Promise.all([
          fetch(`${backendBaseUrl}/treatment/external-exercises/ascend/exercise-types/`, requestOptions),
          fetch(`${backendBaseUrl}/treatment/external-exercises/ascend/equipments/`, requestOptions),
          fetch(`${backendBaseUrl}/treatment/external-exercises/ascend/bodyparts/`, requestOptions),
        ]);
        const [typesPayload, equipmentsPayload, bodyPartsPayload] = await Promise.all([
          typesResponse.json().catch(() => ({})),
          equipmentsResponse.json().catch(() => ({})),
          bodyPartsResponse.json().catch(() => ({})),
        ]);
        if (!typesResponse.ok || !equipmentsResponse.ok || !bodyPartsResponse.ok) {
          throw new Error('Unable to load Ascend filter metadata.');
        }
        setAscendExerciseTypes((Array.isArray(typesPayload.exerciseTypes) ? typesPayload.exerciseTypes : [])
          .map((type) => typeof type === 'string' ? type : type?.name)
          .filter(Boolean));
        setAscendEquipments((Array.isArray(equipmentsPayload.equipments) ? equipmentsPayload.equipments : [])
          .map((equipment) => typeof equipment === 'string' ? equipment : equipment?.name)
          .filter(Boolean));
        setAscendRegions([...new Set((Array.isArray(bodyPartsPayload.bodyParts) ? bodyPartsPayload.bodyParts : [])
          .map((bodyPart) => typeof bodyPart === 'string' ? bodyPart : bodyPart?.name)
          .filter(Boolean))].sort());
      } catch (error) {
        if (error.name !== 'AbortError') {
          setAscendExerciseTypes([]);
          setAscendEquipments([]);
          setAscendRegions([]);
        }
      }
    };

    loadMetadata();
    return () => controller.abort();
  }, [exerciseSource, token]);

  useEffect(() => {
    if (exerciseSource !== 'musclewiki') {
      return undefined;
    }

    const authToken = sessionStorage.getItem('token') || token;
    if (!authToken) {
      setMuscleWikiFilters({});
      setMuscleWikiCategories([]);
      setMuscleWikiMuscles([]);
      return undefined;
    }

    const controller = new AbortController();
    const loadMetadata = async () => {
      try {
        const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
        const requestOptions = { headers: { Authorization: `Token ${authToken}` }, signal: controller.signal };
        const [filtersResponse, categoriesResponse, musclesResponse] = await Promise.all([
          fetch(`${backendBaseUrl}/treatment/external-exercises/musclewiki/filters/`, requestOptions),
          fetch(`${backendBaseUrl}/treatment/external-exercises/musclewiki/categories/`, requestOptions),
          fetch(`${backendBaseUrl}/treatment/external-exercises/musclewiki/muscles/`, requestOptions),
        ]);
        const [filtersPayload, categoriesPayload, musclesPayload] = await Promise.all([
          filtersResponse.json().catch(() => ({})),
          categoriesResponse.json().catch(() => ({})),
          musclesResponse.json().catch(() => ({})),
        ]);
        if (!filtersResponse.ok || !categoriesResponse.ok || !musclesResponse.ok) {
          throw new Error('Unable to load MuscleWiki filter metadata.');
        }
        setMuscleWikiFilters(filtersPayload.filters || {});
        setMuscleWikiCategories(Array.isArray(categoriesPayload.categories) ? categoriesPayload.categories : []);
        setMuscleWikiMuscles(Array.isArray(musclesPayload.muscles) ? musclesPayload.muscles : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setMuscleWikiFilters({});
          setMuscleWikiCategories([]);
          setMuscleWikiMuscles([]);
        }
      }
    };

    loadMetadata();
    return () => controller.abort();
  }, [exerciseSource, token]);

  useEffect(() => {
    if (exerciseSource !== 'ascend') {
      setExternalSearchError('');
      return undefined;
    }

    const query = searchInput.trim();
    const hasDropdownFilter = Boolean(filterType || filterLevel || filterEquipment || filterBodyRegion);

    const storedToken = sessionStorage.getItem('token');
    const authToken = storedToken || token;
    if (!authToken) {
      setExternalExercises([]);
      setExternalSearchError('Your session has expired. Please sign in again.');
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setIsLoading(true);
      setExternalSearchError('');
      try {
        const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
        const filterParams = new URLSearchParams();
        if (query) filterParams.set('name', query);
        if (filterType) filterParams.set('exerciseType', filterType);
        if (filterLevel) filterParams.set('level', filterLevel);
        if (filterEquipment) filterParams.set('equipment', filterEquipment);
        if (filterBodyRegion) filterParams.set('bodyPart', filterBodyRegion);
        const pageSize = 50;
        filterParams.set('limit', pageSize);
        if (ascendCursor) filterParams.set('after', ascendCursor);
        const endpoint = hasDropdownFilter || !query
          ? `/treatment/external-exercises/ascend/filter/?${filterParams.toString()}`
          : `/treatment/external-exercises/ascend/search/?search=${encodeURIComponent(query)}`;
        const response = await fetch(
          `${backendBaseUrl}${endpoint}`,
          {
            headers: { Authorization: `Token ${authToken}` },
            signal: controller.signal,
          }
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || 'Unable to search the Ascend exercise library.');
        }
        const results = Array.isArray(payload.exercises) ? payload.exercises : [];
        const resolvedResults = results.map((exercise) => ({
          ...exercise,
          isExternal: true,
          img: exercise.pictureURL || '',
          video: exercise.videoURL || '',
          video_url: exercise.videoURL || '',
        }));
        setExternalExercises(previous => ascendCursor
          ? [...previous, ...resolvedResults.filter(exercise => !previous.some(existing => existing.sourceExerciseId === exercise.sourceExerciseId))]
          : resolvedResults);
        const nextCursor = payload.pagination?.nextCursor || null;
        setAscendNextCursor(nextCursor);
        setAscendHasMore(Boolean(payload.pagination?.hasNextPage && nextCursor));
      } catch (error) {
        if (error.name !== 'AbortError') {
          if (!ascendCursor) setExternalExercises([]);
          setExternalSearchError(error.message || 'Unable to search the Ascend exercise library.');
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, 300);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [exerciseSource, searchInput, filterType, filterLevel, filterEquipment, filterBodyRegion, token, ascendCursor]);

  useEffect(() => {
    if (exerciseSource !== 'musclewiki') {
      return undefined;
    }

    const query = searchInput.trim();
    const authToken = sessionStorage.getItem('token') || token;
    if (!authToken) {
      setMuscleWikiExercises([]);
      setExternalSearchError('Your session has expired. Please sign in again.');
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setIsLoading(true);
      setExternalSearchError('');
      try {
        const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
        const filterParams = new URLSearchParams();
        if (filterType) filterParams.set('force', filterType);
        if (filterLevel) filterParams.set('difficulty', filterLevel);
        if (filterEquipment) filterParams.set('category', filterEquipment);
        if (filterBodyRegion) filterParams.set('muscles', filterBodyRegion);
        const pageSize = MUSCLEWIKI_PAGE_SIZE;
        filterParams.set('limit', pageSize);
        filterParams.set('offset', muscleWikiOffset);
        const endpoint = query.length >= 2
          ? `/treatment/external-exercises/musclewiki/search/?search=${encodeURIComponent(query)}&${filterParams.toString()}`
          : `/treatment/external-exercises/musclewiki/filter/?${filterParams.toString()}`;
        const response = await fetch(`${backendBaseUrl}${endpoint}`, {
          headers: { Authorization: `Token ${authToken}` },
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Unable to browse MuscleWiki exercises.');
        const results = Array.isArray(payload.exercises) ? payload.exercises : [];
        const recentById = new Map(recentMuscleWikiExercisesRef.current.map(exercise => [exercise.sourceExerciseId, exercise]));
        const resolvedResults = results.map((exercise) => ({
          ...exercise,
          isExternal: true,
          img: recentById.get(exercise.sourceExerciseId)?.img || exercise.pictureURL || '',
          video: exercise.videoURL || '',
          video_url: exercise.videoURL || '',
        }));
        resolvedResults.sort((left, right) => Number(Boolean(right.img)) - Number(Boolean(left.img)));
        setMuscleWikiExercises(previous => muscleWikiOffset === 0
          ? resolvedResults
          : [...previous, ...resolvedResults.filter(exercise => !previous.some(existing => existing.sourceExerciseId === exercise.sourceExerciseId))]);
        const pagination = payload.pagination || {};
        setMuscleWikiHasMore(muscleWikiOffset + results.length < Number(pagination.total || 0));
      } catch (error) {
        if (error.name !== 'AbortError') {
          if (muscleWikiOffset === 0) setMuscleWikiExercises([]);
          setMuscleWikiHasMore(false);
          setExternalSearchError(error.message || 'Unable to browse MuscleWiki exercises.');
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, 300);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [exerciseSource, searchInput, filterType, filterLevel, filterEquipment, filterBodyRegion, token, muscleWikiOffset]);

  useEffect(() => {
    if (exercises !== undefined) {
      let filtered = exercises.filter(exercise =>
        exercise.title.toLowerCase().includes(searchInput.toLowerCase())
      );
      if (filterType && exerciseSource === 'internal') filtered = filtered.filter(ex => ex.exerciseType === filterType);
      if (filterLevel && exerciseSource === 'internal') filtered = filtered.filter(ex => String(ex.exerciseLevel) === filterLevel);
      if (filterEquipment && exerciseSource === 'internal') filtered = filtered.filter(ex =>
        Array.isArray(ex.equipments) ? ex.equipments.includes(filterEquipment) : ex.equipment === filterEquipment
      );
      if (filterBodyRegion && exerciseSource === 'internal') filtered = filtered.filter(ex =>
        Array.isArray(ex.bodyRegion)
          ? ex.bodyRegion.includes(filterBodyRegion)
          : ex.bodyRegion === filterBodyRegion
      );
      setFilteredExercises([...new Set(filtered)]);
    }
  }, [exercises, exerciseSource, searchInput, filterType, filterLevel, filterEquipment, filterBodyRegion]);

  const uniqueTypes = useMemo(() => [
    ...new Set(exercises.filter(ex => ex.exerciseType).map(ex => ex.exerciseType))
  ], [exercises]);
  const typeOptions = exerciseSource === 'ascend'
    ? ascendExerciseTypes
    : exerciseSource === 'musclewiki' ? (muscleWikiFilters.forces || []) : uniqueTypes;
  const uniqueLevels = useMemo(() => [
    ...new Set(exercises.filter(ex => ex.exerciseLevel != null).map(ex => String(ex.exerciseLevel)))
  ].sort((a, b) => Number(a) - Number(b)), [exercises]);
  const uniqueEquipment = useMemo(() => [
    ...new Set(exercises.filter(ex => ex.equipment).map(ex => ex.equipment))
  ], [exercises]);
  const equipmentOptions = exerciseSource === 'ascend'
    ? ascendEquipments
    : exerciseSource === 'musclewiki'
      ? muscleWikiCategories.map(category => typeof category === 'string' ? category : category?.name).filter(Boolean)
      : uniqueEquipment;
  const uniqueBodyRegions = useMemo(() => [
    ...new Set(exercises.flatMap(ex => Array.isArray(ex.bodyRegion) ? ex.bodyRegion : ex.bodyRegion ? [ex.bodyRegion] : []))
  ].sort(), [exercises]);
  const regionOptions = exerciseSource === 'ascend'
    ? ascendRegions
    : exerciseSource === 'musclewiki'
      ? muscleWikiMuscles.map(muscle => typeof muscle === 'string' ? muscle : muscle?.name).filter(Boolean)
      : uniqueBodyRegions;

  // Open modal and initialize params when Add is clicked
  const handleOpenEditModal = async (exercise) => {
    let selectedExercise = exercise;

    if (exercise.isExternal && ['ascend', 'musclewiki'].includes(exercise.source) && exercise.sourceExerciseId) {
      setIsLoading(true);
      try {
        const authToken = sessionStorage.getItem('token') || token;
        const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
        const response = await fetch(
          `${backendBaseUrl}/treatment/external-exercises/${exercise.source}/${encodeURIComponent(exercise.sourceExerciseId)}/`,
          { headers: { Authorization: `Token ${authToken}` } }
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || 'Unable to load the full Ascend exercise details.');
        }
        let mediaPayload = {};
        if (exercise.source === 'musclewiki') {
          const mediaResponse = await fetch(
            `${backendBaseUrl}/treatment/external-exercises/musclewiki/${encodeURIComponent(exercise.sourceExerciseId)}/media/`,
            { headers: { Authorization: `Token ${authToken}` } }
          );
          mediaPayload = await mediaResponse.json().catch(() => ({}));
          if (!mediaResponse.ok) {
            mediaPayload = {};
          }
        }
        selectedExercise = {
          ...exercise,
          ...payload,
          isExternal: true,
          img: mediaPayload.pictureURL || payload.pictureURL || exercise.img || '',
          video: mediaPayload.videoURL || payload.videoURL || exercise.video || '',
          video_url: mediaPayload.videoURL || payload.videoURL || exercise.video_url || '',
        };
        rememberMuscleWikiExercise(selectedExercise);
      } catch (error) {
        setExternalSearchError(error.message || 'Unable to load the full Ascend exercise details.');
      } finally {
        setIsLoading(false);
      }
    }

    setSelectedExercise(selectedExercise);
    setEditedParams({
      sets: selectedExercise.sets ?? 3,
      repetitions: selectedExercise.repetitions ?? 10,
      repetitionMaximum: selectedExercise.repetitionMaximum ?? 1,
      weightKg: selectedExercise.weightKg ?? 0,
      weightTotalKg: selectedExercise.weightTotalKg ?? 0,
      weightLbs: selectedExercise.weightLbs ?? 0,
      weightTotalLbs: selectedExercise.weightTotalLbs ?? 0,
      time: typeof selectedExercise.time === "string" ? parseInt(selectedExercise.time) || parseInt((selectedExercise.time || "").split("_")[0]) || 30 : selectedExercise.time ?? 30,
      pause: typeof selectedExercise.pause === "string" ? parseInt(selectedExercise.pause) || parseInt((selectedExercise.pause || "").split("_")[0]) || 15 : selectedExercise.pause ?? 15,
      frequencyType: selectedExercise.frequencyType ?? "daily",
      frequency: selectedExercise.frequency ?? 1,
      index: selectedExercise.index ?? 1,
      description: selectedExercise.description ?? "",
      title: selectedExercise.title ?? "",
      img: selectedExercise.img ?? selectedExercise.picture ?? selectedExercise.pictureURL ?? selectedExercise.image_url ?? "",
      video: selectedExercise.video ?? selectedExercise.videoURL ?? selectedExercise.video_url ?? "",
      patientInMedia: typeof selectedExercise.patientInMedia === 'boolean' ? selectedExercise.patientInMedia : null,
      doseType: selectedExercise.doseType || ((selectedExercise.time && selectedExercise.time !== 0) ? "time" : "repetitions"),
    });
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditModalViewOnly(false);
    setSelectedExercise(null);
  };

  const handleParamChange = (field, value) => {
    // For time and pause, always store as integer
    if (field === "time" || field === "pause") {
      setEditedParams(prev => ({ ...prev, [field]: parseInt(value, 10) || 0 }));
    } else {
      setEditedParams(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleConfirmAdd = () => {
    if (selectedExercise) {
      onAddExercise({ ...selectedExercise, ...editedParams });
      setEditModalOpen(false);
      setSelectedExercise(null);
    }
  };

  const handleToggleSelect = (e, exerciseId) => {
    if (e) e.stopPropagation();
    setSelectedExerciseIds(prev => {
      const next = new Set(prev);
      if (next.has(exerciseId)) next.delete(exerciseId);
      else next.add(exerciseId);
      return next;
    });
  };

  const handleOpenQueueModal = () => {
    const queue = filteredExercises.filter(ex => selectedExerciseIds.has(ex.id || ex.identifier));
    if (queue.length === 0) return;
    if (queue.length === 1) {
      handleOpenEditModal(queue[0]);
      setSelectedExerciseIds(new Set());
      return;
    }
    setQueueModalOpen(true);
  };

  const handleConfirmAll = (configuredExercises) => {
    configuredExercises.forEach(ex => onAddExercise(ex));
    setQueueModalOpen(false);
    setSelectedExerciseIds(new Set());
  };

  const handleExerciseSelect = (exercise) => {
    if (isPickerMode) {
      onExerciseSelect(exercise);
      return;
    }

    handleOpenEditModal(exercise);
  };

  let didCancel = false;

  //const fetchExercises = async () => {
  //  try {
  //    setIsLoading(true);
  //    const response = await fetch("https://api.virtus-one.com/treatment/all_exercises/",  {
  //      headers: {
  //        "Authorization": `Token ${token}`
  //      },
  //      method: "GET",
  //    });
//  
  //    if (response.ok) {
  //      const data = await response.json();
  //      console.log(data)
  //      return data;
  //    } else {
  //      throw new Error("Failed to fetch exercises");
  //    }
  //  } catch (error) {
  //    if (!didCancel) {
  //      setIsLoading(false);
  //      alert("ERROR: " + error.message);
  //    }
  //  }
  //};

  // Library exercises are provided by parent via props; no fetch here to avoid duplicate calls.
  // Filtering is handled by the main useEffect above.
  
  

  
  
  
  return (
    <Card
      className={isPickerMode ? classes.pickerRootCard : undefined}
      style={isPickerMode
        ? { background: '#f7faff', borderRadius: 16, boxShadow: '0 6px 32px rgba(0,45,85,0.18)' }
        : { background: '#f7faff', borderRadius: 16, paddingBottom: '3%', boxShadow: '0 6px 32px rgba(0,45,85,0.18)', overflow: 'hidden' }}
    >
      <CssBaseline />
      <div className={classes.libraryHeader}>
        <Typography variant="h6" className={classes.libraryHeaderTitle}>
          {title}
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="center" style={{ flex: 1 }}>
          <ExerciseSearchbar
            exercises={exercises}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            fullWidth={isPickerMode}
          />
        </Box>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '8px 10px', marginBottom: 8 }}>
        <Select disableUnderline value={exerciseSource} onChange={e => handleExerciseSourceChange(e.target.value)} className={classes.orangeSelect}
          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}>
          <MenuItem value="internal" className={classes.menuItem}>My Library</MenuItem>
          <MenuItem value="ascend" className={classes.menuItem}>Ascend Live</MenuItem>
          <MenuItem value="musclewiki" className={classes.menuItem}>MuscleWiki Live</MenuItem>
        </Select>
        <Select disableUnderline displayEmpty value={filterType} onChange={e => setFilterType(e.target.value)} className={classes.orangeSelect}
          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}>
          <MenuItem value="" className={classes.menuItem}><em>All Types</em></MenuItem>
          {typeOptions.map(t => <MenuItem key={t} value={t} className={classes.menuItem}>{t}</MenuItem>)}
        </Select>
        <Select disableUnderline displayEmpty value={filterLevel} onChange={e => setFilterLevel(e.target.value)} className={classes.orangeSelect}
          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}>
          <MenuItem value="" className={classes.menuItem}><em>All Levels</em></MenuItem>
          {(exerciseSource === 'musclewiki' ? muscleWikiFilters.difficulties || [] : uniqueLevels).map(l => <MenuItem key={l} value={l} className={classes.menuItem}>{exerciseSource === 'musclewiki' ? l : `Level ${l}`}</MenuItem>)}
        </Select>
        <Select disableUnderline displayEmpty value={filterEquipment} onChange={e => setFilterEquipment(e.target.value)} className={classes.orangeSelect}
          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}>
          <MenuItem value="" className={classes.menuItem}><em>All Equipment</em></MenuItem>
          {equipmentOptions.map(eq => <MenuItem key={eq} value={eq} className={classes.menuItem}>{eq}</MenuItem>)}
        </Select>
        <Select disableUnderline displayEmpty value={filterBodyRegion} onChange={e => setFilterBodyRegion(e.target.value)} className={classes.orangeSelect}
          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}>
          <MenuItem value="" className={classes.menuItem}><em>All Regions</em></MenuItem>
          {regionOptions.map(r => <MenuItem key={r} value={r} className={classes.menuItem}>{r}</MenuItem>)}
        </Select>
        {!isPickerMode && selectedExerciseIds.size > 0 && (
          <Box display="flex" alignItems="center" style={{ marginLeft: 'auto', gap: 8 }}>
            <Typography style={{ color: '#002D55', fontSize: 13, fontWeight: 600 }}>
              {selectedExerciseIds.size} selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              style={{ backgroundColor: '#FF7F27', color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
              onClick={handleOpenQueueModal}
            >
              Add Selected
            </Button>
            <Button
              size="small"
              style={{ color: '#002D55', textTransform: 'none', padding: '2px 8px' }}
              onClick={() => setSelectedExerciseIds(new Set())}
            >
              Clear
            </Button>
          </Box>
        )}
      </div>
      <main className={isPickerMode ? classes.pickerMain : undefined}>
        {/* Hero unit */}
        <Container className={classes.cardGrid} maxWidth="xl" style={{ minHeight: '50vh' }}>
          {/* End hero unit */}
          <Grid container spacing={2} >
            {isLoading && filteredExercises.length === 0 ? (
              Array.from({ length: 6 }).map((_, index) => (
                <Grid item key={index} xs={6} sm={4} md={4} lg={3} xl={2}>
                  <Skeleton variant="rect" height={190} style={{ borderRadius: 18 }} />
                </Grid>
              ))
            ) : externalSearchError ? (
              <Grid item xs={12}>
                <Typography style={{ color: '#b42318', textAlign: 'center', padding: '40px 0', fontSize: 'clamp(0.8rem, 1.2vw, 1.01rem)' }}>
                  {externalSearchError}
                </Typography>
              </Grid>
            ) : filteredExercises.length === 0 ? (
              <Grid item xs={12}>
                <Typography style={{ color: '#002D55', opacity: 0.4, textAlign: 'center', padding: '40px 0', fontSize: 'clamp(0.8rem, 1.2vw, 1.01rem)' }}>
                  {exerciseSource === 'ascend' && !searchInput.trim()
                    ? 'Search Ascend to find live exercises.'
                    : 'No exercises match the current filters.'}
                </Typography>
              </Grid>
            ) : filteredExercises.map((exercise) => (
              <Grid item key={exercise.id || exercise.identifier} xs={6} sm={4} md={4} lg={3} xl={2} >
                <div style={{ position: 'relative' }}>
                  {!isPickerMode && (
                    <div style={{
                      position: 'absolute', top: 9, left: 9, zIndex: 2,
                      borderRadius: 16,
                      overflow: 'hidden', lineHeight: 0,
                    }}>
                      <Checkbox
                        checked={selectedExerciseIds.has(exercise.id || exercise.identifier)}
                        onChange={e => handleToggleSelect(e, exercise.id || exercise.identifier)}
                        onClick={e => e.stopPropagation()}
                        className={classes.largeCheckbox}
                        size="small"
                        icon={
                          <span style={{
                            display: 'inline-block', boxSizing: 'border-box',
                            width: '1em', height: '1em',
                            border: '2px solid currentColor', borderRadius: 16,
                          }} />
                        }
                        checkedIcon={
                          <span style={{
                            display: 'inline-flex', boxSizing: 'border-box',
                            width: '1em', height: '1em',
                            background: 'currentColor', borderRadius: 16,
                            alignItems: 'center', justifyContent: 'center',
                          }}>
                            <svg viewBox="0 0 24 24" style={{ width: '0.7em', height: '0.7em', fill: '#fff' }}>
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                          </span>
                        }
                        style={{
                          padding: 4, fontSize: 'clamp(13px, 1.2vw, 23px)', color: selectedExerciseIds.has(exercise.id || exercise.identifier) ? '#0fbb51' : '#002D55',
                        }}
                      />
                    </div>
                  )}
                  <Button
                    size="small"
                    className={classes.addButton}
                    onClick={e => {
                      e.stopPropagation();
                      if (isPickerMode) {
                        handleExerciseSelect(exercise);
                        return;
                      }
                      handleOpenEditModal(exercise);
                      setEditModalViewOnly(selectedExerciseIds.size > 0);
                    }}
                    style={{ position: 'absolute', top: 9, right: 9, zIndex: 2, minWidth: 0, padding: '2px 7px', marginBottom: 0, fontSize: 'clamp(0.45rem, 0.7vw, 0.82rem)' }}
                  >
                    {isPickerMode ? selectionActionLabel : 'View'}
                  </Button>
                  <Card
                    className={`${classes.cardOriginal} ${classes.cardClickable}`}
                    onClick={() => {
                      if (!isPickerMode && selectedExerciseIds.size > 0) {
                        handleToggleSelect(null, exercise.id || exercise.identifier);
                      } else {
                        handleExerciseSelect(exercise);
                      }
                    }}
                    style={selectedExerciseIds.has(exercise.id || exercise.identifier) ? { boxShadow: '0 0 0 2px #FF7F27' } : {}}
                  >
                    <div style={{ padding: 'clamp(7px, 0.63vw, 12px)', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'clamp(87px, 7.5vw, 138px)' }}>
                      {exercise.img ? (
                        <img alt={exercise.title} src={exercise.img} style={{ height: 'clamp(47px, 5.4vw, 104px)', borderRadius: 8, objectFit: 'cover' }} />
                      ) : (
                        <Skeleton variant="rect" style={{ width: 'clamp(87px, 7.5vw, 138px)', height: 'clamp(47px, 5.4vw, 104px)', borderRadius: 8 }} />
                      )}
                    </div>
                    <CardContent className={classes.cardContent} style={{ flex: 1, minHeight: 'clamp(86px, calc(14px + 5.25vw), 115px)', backgroundColor: '#F0F8FF' }}>
                        <Typography gutterBottom className={classes.exerciseTypographyCardStyling} >
                          {exercise.title}
                        </Typography>
                      <Grid container xs={12}>
                        
                      </Grid>
                    </CardContent>
                  </Card>
                </div>
              </Grid>
            ))}
          </Grid>
          {isLoading && filteredExercises.length > 0 && (
            <Box display="flex" justifyContent="center" pt={3}>
              <CircularProgress size={24} style={{ color: '#FF7F27' }} />
            </Box>
          )}
          {exerciseSource === 'musclewiki' && muscleWikiHasMore && !isLoading && (
            <Box display="flex" justifyContent="center" pt={3} pb={1}>
              <Button
                variant="outlined"
                onClick={() => setMuscleWikiOffset(currentOffset => currentOffset + MUSCLEWIKI_PAGE_SIZE)}
                style={{ borderColor: '#FF7F27', color: '#002D55', borderRadius: 8, fontWeight: 700 }}
              >
                Load more exercises
              </Button>
            </Box>
          )}
          {exerciseSource === 'ascend' && ascendHasMore && !isLoading && (
            <Box display="flex" justifyContent="center" pt={3} pb={1}>
              <Button
                variant="outlined"
                onClick={() => setAscendCursor(ascendNextCursor)}
                style={{ borderColor: '#FF7F27', color: '#002D55', borderRadius: 8, fontWeight: 700 }}
              >
                Load more exercises
              </Button>
            </Box>
          )}
        </Container>
        {!isPickerMode && (
          <>
            <ExerciseEditDialog
              open={editModalOpen}
              onClose={handleCloseEditModal}
              onConfirm={handleConfirmAdd}
              selectedExercise={selectedExercise}
              editedParams={editedParams}
              onParamChange={handleParamChange}
              dialogTitle="Add Exercise to Program"
              viewOnly={editModalViewOnly}
              libraryExercises={exercises}
              phoneUploadContext={phoneUploadContext ? { ...phoneUploadContext, allowDraftUpload: true } : null}
              forceMediaDrawer
            />
            <ExerciseEditDialog
              open={queueModalOpen}
              onClose={() => { setQueueModalOpen(false); setSelectedExerciseIds(new Set()); }}
              exerciseQueue={filteredExercises.filter(ex => selectedExerciseIds.has(ex.id || ex.identifier))}
              onConfirmAll={handleConfirmAll}
              selectedExercise={null}
              editedParams={{}}
              onParamChange={() => {}}
              dialogTitle="Add Exercises to Program"
              libraryExercises={exercises}
              phoneUploadContext={phoneUploadContext}
            />
          </>
        )}
      </main>
    </Card>
  );
} 